import { CalculationEngineMode } from 'constants/flags';

const ENGINE_MODES: readonly CalculationEngineMode[] = ['legacy', 'shadow', 'totalfinance'];

export const isCalculationEngineMode = (value: unknown): value is CalculationEngineMode =>
  typeof value === 'string' && (ENGINE_MODES as readonly string[]).includes(value);

/**
 * Resolve a per-environment override against the code default in `constants/flags`.
 * Unset or empty keeps the code default; a recognised mode wins; anything else is treated as a
 * mistyped rollback and selects 'legacy', never a TotalFinance candidate.
 */
export function resolveCalculationEngineMode(
  override: string | undefined,
  codeDefault: CalculationEngineMode,
): CalculationEngineMode {
  const value = override?.trim();
  if (!value) return codeDefault;
  return isCalculationEngineMode(value) ? value : 'legacy';
}

/**
 * Resolve a per-environment on/off override against its code default. Unset or empty keeps the
 * code default; `on`/`true`/`1` and `off`/`false`/`0` set it; anything else is a typo and selects
 * `whenUnrecognised`, the switch's safe position.
 */
export function resolveSwitch(
  override: string | undefined,
  codeDefault: boolean,
  whenUnrecognised: boolean,
): boolean {
  const value = override?.trim().toLowerCase();
  if (!value) return codeDefault;
  if (['on', 'true', '1'].includes(value)) return true;
  if (['off', 'false', '0'].includes(value)) return false;
  return whenUnrecognised;
}

/** A legacy guard override: a mistyped value keeps the guard on, so it never removes a check. */
export const resolveLegacyGuard = (override: string | undefined, codeDefault: boolean): boolean =>
  resolveSwitch(override, codeDefault, true);

/**
 * Whether engine diagnostics are shown on the page outside shadow mode. Off by default so a
 * TotalFinance build looks like any other to users; NEXT_PUBLIC_TOTALFINANCE_DIAGNOSTICS=1 (build
 * time, like the modes) shows them for dogfooding and acceptance.
 */
export const isTotalFinanceDiagnosticsEnabled = (
  value: string | undefined = process.env.NEXT_PUBLIC_TOTALFINANCE_DIAGNOSTICS,
): boolean => ['1', 'true'].includes(value?.trim().toLowerCase() ?? '');
